// src/pages/analytics/CcFeeTab.tsx
//
// CC Fee reconciliation (recipe cc-fee-recon, flag analytics.ccfee, agent
// 3.20.0+) — card fees charged to customers in Advantage vs processing fees
// paid in QuickBooks, by month. The PBI "CCFee" page is broken in their
// workspace; we have both sources. Without the QuickBooks module the paid
// side shows as unavailable.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CreditCard, RefreshCw, Download, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { apiV2 as api } from '../../services/apiV2';
import type { CcFeeRow } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const MONTHS = [6, 12, 24];
const pill = (on: boolean) => `px-2 py-1.5 text-[11px] rounded-md border transition-all ${
  on ? 'bg-blue-500/15 text-blue-200 border-blue-500/40'
     : 'border-slate-700/50 text-slate-500 hover:text-slate-300 bg-slate-900/40'}`;
const btn = 'px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40';
const tooltipProps = {
  contentStyle: { backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, fontSize: 12 },
  labelStyle: { color: '#e2e8f0' },
};

function exportRows(rows: CcFeeRow[], exporter: typeof exportCsv = exportCsv) {
  const cols: CsvColumns<CcFeeRow> = [
    ['Month',            (r) => r.month],
    ['Card Sales',       (r) => r.cardSales],
    ['Card Invoices',    (r) => r.cardInvoices],
    ['Fees Charged',     (r) => r.charged],
    ['Charged % of Card Sales', (r) => r.chargedPct],
    ['Fees Paid (QB)',   (r) => r.paid ?? ''],
    ['Net',              (r) => r.net ?? ''],
    ['Recovery %',       (r) => r.recoveryPct ?? ''],
  ];
  exporter('cc-fee-reconciliation', cols, rows);
}

export function CcFeeTab() {
  const [months, setMonths] = useState(12);
  const q = useQuery({
    queryKey: ['analyticsCcFee', months],
    queryFn: () => api.getCcFeeRecon({ months }),
    staleTime: 60_000,
    retry: 1,
  });
  const data = q.data;
  const rows = data?.rows ?? [];
  const t = data?.totals;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2"><CreditCard size={20} /> CC Fees</h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Card fees charged to customers (Advantage) vs processing fees paid (QuickBooks).
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Months</span>
          <div className="flex items-center gap-1.5">
            {MONTHS.map(m => <button key={m} onClick={() => setMonths(m)} className={pill(months === m)}>{m}</button>)}
          </div>
        </div>
        <div className="flex-1" />
        <button onClick={() => exportRows(rows)} disabled={rows.length === 0} className={btn}><Download size={11} /> CSV</button>
        <button onClick={() => exportRows(rows, exportXlsx)} disabled={rows.length === 0} className={btn}><FileSpreadsheet size={11} /> Excel</button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load CC fees</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && t && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Fees charged', value: fmtCur(t.charged), sub: `${t.cardInvoices.toLocaleString()} card invoices` },
              { label: 'Fees paid (QB)', value: t.paid == null ? '—' : fmtCur(t.paid), sub: data.qbAvailable ? `${data.paidAccounts.length} account${data.paidAccounts.length === 1 ? '' : 's'}` : 'QuickBooks not synced' },
              { label: 'Net', value: t.net == null ? '—' : fmtCur(t.net), sub: t.net == null ? '' : t.net >= 0 ? 'customers covered the fees' : 'fees absorbed', tone: t.net == null ? '' : t.net >= 0 ? 'text-emerald-400' : 'text-red-400' },
              { label: 'Recovery', value: t.recoveryPct == null ? '—' : `${t.recoveryPct.toFixed(1)}%`, sub: 'charged ÷ paid' },
            ].map(k => (
              <div key={k.label} className="p-3 rounded-xl border border-slate-700/50 bg-slate-800/30">
                <div className="text-xs text-slate-400 uppercase tracking-wider">{k.label}</div>
                <div className={`text-xl font-semibold tabular-nums mt-1 ${k.tone || 'text-white'}`}>{k.value}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{k.sub}</div>
              </div>
            ))}
          </div>

          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 p-4">
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 4, right: 8, left: 8, bottom: 4 }}>
                  <CartesianGrid vertical={false} stroke="#334155" strokeOpacity={0.4} />
                  <XAxis dataKey="month" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={{ stroke: '#475569' }} tickLine={false}
                    tickFormatter={(v) => `$${(Number(v) / 1000).toFixed(1)}k`} />
                  <Tooltip {...tooltipProps} formatter={(value, name) => [fmtCur(Number(value)), String(name)]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="charged" name="Charged (Advantage)" fill="#3b82f6" radius={[3, 3, 0, 0]} maxBarSize={28} />
                  {data.qbAvailable && <Bar dataKey="paid" name="Paid (QuickBooks)" fill="#f59e0b" radius={[3, 3, 0, 0]} maxBarSize={28} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    {['Month', 'Card Sales', 'Card Inv.', 'Charged', '% of Card $', 'Paid (QB)', 'Net', 'Recovery'].map((h, i) => (
                      <th key={h} className={`px-3 py-2.5 ${i === 0 ? 'text-left' : 'text-right'} text-slate-400 font-medium whitespace-nowrap`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => (
                    <tr key={r.month} className="border-b border-slate-800/50 hover:bg-slate-700/20">
                      <td className="px-3 py-2 text-white">{r.month}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-300">{fmtCur(r.cardSales)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-400">{r.cardInvoices.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-white">{fmtCur(r.charged)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-400">{r.chargedPct.toFixed(2)}%</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-300">{r.paid == null ? '—' : fmtCur(r.paid)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.net == null ? 'text-slate-500' : r.net >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{r.net == null ? '—' : fmtCur(r.net)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-300">{r.recoveryPct == null ? '—' : `${r.recoveryPct.toFixed(1)}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-2 border-t border-slate-700/30 text-[11px] text-slate-500">
              {data.qbAvailable
                ? <>Paid = QuickBooks expense accounts matching: {data.accountPatterns.join(', ')}{data.paidAccounts.length > 0 && <> ({data.paidAccounts.join(', ')})</>}. Change the match list with the analytics.ccfee_accounts setting.</>
                : 'QuickBooks data unavailable — only the charged side is shown.'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
